import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { APP_NAME } from '../constants';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getCurrentUser, User } from '../services/authService';
import { canManageStaff, getPermissionErrorMessage } from '../services/staffPermissionService';
import TopBar from '../components/TopBar';
import Button from '../components/Button';
import Input from '../components/Input';

interface Business {
  id: string;
  name: string;
}

const Container = styled.div`
  padding: 0 24px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  @media (min-width: 768px) {
    padding: 0 40px;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
`;

const PageTitle = styled.h1`
  font-size: 38px;
  font-weight: 800;
  margin-bottom: 16px;
  color: #333;
  
  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const Breadcrumbs = styled.div`
  display: flex;
  margin-bottom: 20px;
  font-size: 14px;
  color: #555;
  width: 100%;
  overflow-x: hidden;
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const BreadcrumbLink = styled.span`
  cursor: pointer;
  white-space: nowrap;
  
  &:hover {
    text-decoration: underline;
  }
`;

const BreadcrumbSeparator = styled.span`
  margin: 0 8px;
`;

const BreadcrumbText = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100px;
  
  @media (max-width: 480px) {
    max-width: 80px;
  }
`;

const FormContainer = styled.div`
  background-color: white;
  border-radius: 24px;
  padding: 32px;
  margin-bottom: 24px;
  width: 100%;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

const ButtonContainer = styled.div`
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
`;

const ErrorMessage = styled.div`
  color: #e53935;
  padding: 16px;
  background-color: #ffebee;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 14px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 20px;
`;

const PermissionErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 60px 20px;
  background-color: #f9f9f9;
  border-radius: 12px;
  margin-top: 40px;
`;

const PermissionErrorTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
`;

const PermissionErrorMessage = styled.p`
  font-size: 16px;
  color: #666;
  margin-bottom: 32px;
  max-width: 400px;
  line-height: 1.5;
`;

const StaffLookupPage: React.FC = () => {
  const navigate = useNavigate();
  const { businessId } = useParams<{ businessId: string }>();
  const [user, setUser] = useState<User | null>(null);
  const [business, setBusiness] = useState<Business | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasPermission, setHasPermission] = useState(false);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [searching, setSearching] = useState(false);
  const [permissionError, setPermissionError] = useState<string>('');

  useEffect(() => {
    const fetchData = async () => {
      if (!businessId) {
        navigate('/businesses');
        return;
      }

      try {
        setLoading(true);
        setError('');
        setPermissionError('');
        
        const currentUser = await getCurrentUser();
        
        if (!currentUser?.id) {
          navigate('/login');
          return;
        }
        
        setUser(currentUser);

        // Check if user has permission to manage staff
        const canManage = await canManageStaff(currentUser.id, businessId);
        if (!canManage) {
          setPermissionError(getPermissionErrorMessage('add-staff'));
          setLoading(false);
          return;
        }
        
        setHasPermission(true);

        // Fetch business details
        const businessDoc = await getDoc(doc(db, 'businesses', businessId));
        
        if (businessDoc.exists()) {
          setBusiness({
            id: businessDoc.id,
            name: businessDoc.data().name
          });
        } else {
          console.error("Business not found");
          navigate('/businesses');
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('An error occurred while checking permissions');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [businessId, navigate]);

  const handleNavigateToMyAccount = () => {
    navigate('/dashboard');
  };

  const handleNavigateToMyBusinesses = () => {
    navigate('/businesses');
  };
  
  const handleNavigateToSettings = () => {
    navigate(`/business/${businessId}/settings`);
  };
  
  const handleNavigateToStaff = () => {
    navigate(`/business/${businessId}/staff`);
  };
  
  const validateEmail = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return false;
    }
    return true;
  };
  
  const handleLookup = async () => {
    if (!validateEmail() || !businessId) return;
    
    setError('');
    setSearching(true);
    
    try {
      // First check if the user is already a staff member for this business
      const existingStaffQuery = query(
        collection(db, 'staff'),
        where('businessId', '==', businessId),
        where('email', '==', email.toLowerCase())
      );
      
      const existingStaffSnapshot = await getDocs(existingStaffQuery);
      
      if (!existingStaffSnapshot.empty) {
        setError('This user is already a team member for this business');
        setSearching(false);
        return;
      }
      
      // Check if the user exists in the users collection
      const userQuery = query(
        collection(db, 'users'),
        where('email', '==', email.toLowerCase())
      );
      
      const userSnapshot = await getDocs(userQuery);
      
      if (!userSnapshot.empty) {
        // User exists, get document ID (uid) and data
        const userDoc = userSnapshot.docs[0];
        const userId = userDoc.id; // This is the document ID
        const userData = userDoc.data();
        
        navigate(`/business/${businessId}/staff/add-existing`, { 
          state: { 
            userId,
            userData,
            email
          }
        });
      } else {
        // User doesn't exist, navigate to create new user form
        navigate(`/business/${businessId}/staff/add-new`, {
          state: { email }
        });
      }
    } catch (error) {
      console.error('Error during user lookup:', error);
      setError('Failed to look up user. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <ContentWrapper>
          <TopBar user={user} />
          <LoadingContainer>Loading...</LoadingContainer>
        </ContentWrapper>
      </Container>
    );
  }

  // Handle permission error
  if (permissionError) {
    return (
      <>
        <Helmet>
          <title>{APP_NAME} - Access Denied</title>
        </Helmet>
        <Container>
          <ContentWrapper>
            <TopBar user={user} />
            <PermissionErrorContainer>
              <PermissionErrorTitle>Access Denied</PermissionErrorTitle>
              <PermissionErrorMessage>{permissionError}</PermissionErrorMessage>
              <Button 
                text="Back to Team" 
                onClick={() => navigate(`/business/${businessId}/staff`)}
                bgColor="#000"
                textColor="#fff"
                width="auto"
                minWidth="180px"
              />
            </PermissionErrorContainer>
          </ContentWrapper>
        </Container>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{APP_NAME} - Staff Lookup</title>
        <meta name="description" content={`${APP_NAME} - Staff Lookup`} />
      </Helmet>
      <Container>
        <ContentWrapper>
          <TopBar user={user} />
          
          <PageTitle>Add Team Member</PageTitle>
        
          <Breadcrumbs>
            <BreadcrumbLink onClick={handleNavigateToMyAccount}>My Account</BreadcrumbLink>
            <BreadcrumbSeparator>/</BreadcrumbSeparator>
            <BreadcrumbLink onClick={handleNavigateToMyBusinesses}>My Businesses</BreadcrumbLink>
            <BreadcrumbSeparator>/</BreadcrumbSeparator>
            <BreadcrumbLink onClick={handleNavigateToSettings}>
              <BreadcrumbText>{business?.name}</BreadcrumbText>
            </BreadcrumbLink>
            <BreadcrumbSeparator>/</BreadcrumbSeparator>
            <BreadcrumbLink onClick={handleNavigateToStaff}>Team</BreadcrumbLink>
            <BreadcrumbSeparator>/</BreadcrumbSeparator>
            <span>Lookup</span>
          </Breadcrumbs>
        
          {error && <ErrorMessage>{error}</ErrorMessage>}
        
          <FormContainer>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              name="email"
              label="Email Address"
              placeholder="Enter email address"
              required
              secondary={true}
            />
          </FormContainer>
          
          <ButtonContainer>
            <Button
              text="Look Up"
              onClick={handleLookup}
              loading={searching}
              bgColor="#000"
              textColor="#fff"
              width="auto"
              minWidth="140px"
              disabled={!hasPermission}
            />
          </ButtonContainer>
        </ContentWrapper>
      </Container>
    </>
  );
};

export default StaffLookupPage;
